<template>
  <form-container class="model-train-input-container">
    <form-item-container label="モデル名入力">
      <field-container>
        <field-input name="model_name" v-model="modelInfo.modelName" placeholder="ここに学習データファイル名が入ります"
                     validate="required|max:300" validateLabel="モデル名"></field-input>
      </field-container>
    </form-item-container>

    <form-item-container label="学習データ選択" :tip="dsLabelTip">
      <div slot="form-item-annotation">
        <div class="annotation">* 500MBまで
          <template v-if="$route.meta.trainType === 'update'"><br><br>追加/変更する学習データの項目名は、作成時と同じにしてください</template>
        </div>
      </div>
      <field-container :class="{ 'has-error': dsValidateError }">
        <field-upload-ds :url="`${this.$session.get('API_URL')}ds/put`"
                         :ds="modelInfo.ds"
                         :method="$route.meta.trainType"
                         :modelId="modelInfo.modelId"
                         :multiple="$route.meta.trainType === 'update'"
                         :hasPossibility="hasPossibility()"
                         @change="val => modelInfo.ds = val"
                         @before-add="onBeforeAdd"
                         @added="onAdded"
                         @removed="onRemoved"
                         style="width: 100%;"></field-upload-ds>
        <span class="error-message"><strong>学習データを選択してください</strong></span>
      </field-container>
    </form-item-container>

    <form-item-container label="目的選択">
      <field-container :class="{ 'has-error': categoryValidateError }" style="padding: 7px;">
        <div class="category-container">
          <div class="category" :class="{ selected: modelInfo.categoryId === 'CLF' }">
            <p class="category-label">あり/なしやA,B,Cなどのクラスに分類します</p>
            <p class="category-sample">例)来店予測、顧客分類</p>
            <el-button type=primary class="large" @click="onClickCategory('CLF')"
                       :disabled="modelInfo.categoryId !== 'CLF' && $route.meta.trainType === 'update'">
              <font-awesome-icon class="icon" :icon="['fas', 'check-circle']"/>
              分類
            </el-button>
          </div>
          <div class="category" :class="{ selected: modelInfo.categoryId === 'REG' }">
            <p class="category-label">金額などの連続した数値を予測します</p>
            <p class="category-sample">例)売上予測、業績予測</p>
            <el-button type=primary class="large" @click="onClickCategory('REG')"
                       :disabled="modelInfo.categoryId !== 'REG' && $route.meta.trainType === 'update'">
              <font-awesome-icon class="icon" :icon="['fas', 'check-circle']"/>
              回帰
            </el-button>
          </div>
          <div class="category" :class="{ selected: modelInfo.categoryId === 'TSA' }">
            <p class="category-label">過去のデータから未来を予測します</p>
            <p class="category-sample">例)傾向予測</p>
            <el-button type=primary class="large" @click="onClickCategory('TSA')"
                       :disabled="modelInfo.categoryId !== 'TSA' && $route.meta.trainType === 'update'">
              <font-awesome-icon class="icon" :icon="['fas', 'check-circle']"/>
              時系列分析
            </el-button>
          </div>
          <div class="category" :class="{ selected: modelInfo.categoryId === 'UNS' }">
            <p class="category-label">データの背後にある特徴を抽出します</p>
            <p class="category-sample">例)クラスタリング、主成分分析</p>
            <el-button type=primary class="large" @click="onClickCategory('UNS')"
                       :disabled="modelInfo.categoryId !== 'UNS' && $route.meta.trainType === 'update'">
              <font-awesome-icon class="icon" :icon="['fas', 'check-circle']"/>
              教師なし学習
            </el-button>
          </div>
        </div>
        <span class="error-message"><strong>目的を選択してください</strong></span>
      </field-container>
    </form-item-container>

    <form-item-container label="学習データの削除"
                         :annotation="'* モデル' + ($route.meta.trainType === 'train' ? '作成' : '更新') + 'に使用したデータをサーバ上から削除します'">
      <field-container>
        <field-radio-group v-model="modelInfo.deleteUploadFile" name="deleteUploadFile"
                           :options="[{label: '削除しない', value: false}, {label: '削除する', value: true}]"
                           @change="onChangeDeleteDsOption"></field-radio-group>
      </field-container>
    </form-item-container>

  </form-container>
</template>

<script>
import BaseTrainView from './base'
import CommonUtil from '../../../utils/common'
import MessageBox from '../../../utils/message-box'

export default {
  name: 'ModelTrainInputView',
  mixins: [BaseTrainView],
  computed: {
    dsLabelTip () {
      return `・モデルを作成する元となるデータを選択してください。
                         ・ファイル形式は、CSVファイルにのみ対応しています。
                         ・"key"または"id"という項目名のカラムを用意すると、データを識別する項目として選択されます。
                         なければ、各データに連番を付与します。

                         自己回帰アルゴリズムでは、日時を入力するカラムが必要です。
                        ・日時の書式
                        　利用可能な区切り文字と、半角数字8桁(日付)、もしくは半角数字14桁(日時)
                        ・利用可能な区切り文字
                        　/, -, :, 年, 月, 日, 時, 分, 秒
                          例）20180815、2018年08月15日、
                          　　2018/08/15 11:22:33`
    }
  },
  data () {
    return {
      dsValidateError: false,
      categoryValidateError: false,
      uploading: false
    }
  },
  methods: {
    onBeforeAdd (ds, UploadFile) {
      this.uploading = true
    },
    onAdded (ds, addedUploadFile, possibilities) {
      this.uploading = false
      this.dsValidateError = false
      if (this.$route.meta.trainType === 'train') {
        this.$set(this.modelInfo, 'yName', null)
        this.$set(this.modelInfo, 'xType', [])
        this.$set(this.modelInfo, 'xExcept', [])
      }
      this.$set(this.modelInfo, 'figureSummary', null)
      this.$set(this.modelInfo, 'figureDetail', null)
      this.$set(this.modelInfo, 'figureSummaryId', null)
      this.$set(this.modelInfo, 'figureDetailId', null)

      if (this.modelInfo.modelName === '') {
        let fileName = addedUploadFile.fileName
        let extensionIndex = fileName.lastIndexOf('.')
        if (extensionIndex > 0) {
          fileName = fileName.slice(0, extensionIndex)
        }
        this.$set(this.modelInfo, 'modelName', fileName)
      }

      if (CommonUtil.isNotEmpty(possibilities)) {
        this.modelInfo.possibilities = this.handlePossibilitiesResponse(possibilities)
      }

      this.$forceUpdate()
      this.getTrainView().$forceUpdate()
    },
    onRemoved (removeDs, removedUploadFile) {
      this.uploading = false
      if (CommonUtil.isEmpty(removeDs.uploadFiles)) {
        this.dsValidateError = true
        if (this.$route.meta.trainType === 'train') {
          this.$set(this.modelInfo, 'yName', null)
          this.$set(this.modelInfo, 'xType', [])
          this.$set(this.modelInfo, 'xExcept', [])
          this.$set(this.modelInfo, 'dataCollections', null)
          this.$set(this.modelInfo, 'possibilities', null)

          let fileName = removedUploadFile.fileName
          let extensionIndex = fileName.lastIndexOf('.')
          if (extensionIndex > 0) {
            fileName = fileName.slice(0, extensionIndex)
          }
          if (fileName === this.modelInfo.modelName) {
            this.$set(this.modelInfo, 'modelName', '')
          }
        }
      }

      this.$set(this.modelInfo, 'figureSummary', null)
      this.$set(this.modelInfo, 'figureDetail', null)
      this.$set(this.modelInfo, 'figureSummaryId', null)
      this.$set(this.modelInfo, 'figureDetailId', null)
      this.$forceUpdate()
      this.getTrainView().$forceUpdate()
    },
    onClickCategory (categoryId) {
      this.categoryValidateError = false
      this.$set(this.modelInfo, 'categoryId', categoryId)
      this.$set(this.modelInfo, 'algorithm', {
        algorithmId: this.getAlgorithmId(),
        hyperParams: [],
        optimizeParams: true
      })
      this.$set(this.modelInfo, 'transformers', null)
      this.$set(this.modelInfo, 'figureSummary', null)
      this.$set(this.modelInfo, 'figureDetail', null)
      this.$set(this.modelInfo, 'figureSummaryId', null)
      this.$set(this.modelInfo, 'figureDetailId', null)
      this.$set(this.modelInfo, 'transformerPackage', null)
      this.$forceUpdate()
      this.getTrainView().$forceUpdate()
    },
    isValid () {
      if (CommonUtil.isEmpty(this.modelInfo.ds.uploadFiles)) {
        this.dsValidateError = true
      }
      if (CommonUtil.isEmpty(this.modelInfo.categoryId)) {
        this.categoryValidateError = true
      }

      return new Promise((resolve) => {
        this.$validator.validateAll().then((result) => {
          resolve(!this.dsValidateError && !this.categoryValidateError && !this.uploading && result === true)
        })
      })
    },
    onChangeDeleteDsOption () {
      this.$set(this.modelInfo, 'deleteUploadFile', this.modelInfo.deleteUploadFile)
      if (this.modelInfo.deleteUploadFile) {
        MessageBox.confirm(this, '削除すると次回モデル更新には再アップロードが必要となりますがよろしいですか？<br>（セキュリティ上削除が必要な場合などにご利用ください）', {
          dangerouslyUseHTMLString: true
        }).catch(() => {
          this.modelInfo.deleteUploadFile = false
        })
      }
    },
    hasPossibility () {
      return CommonUtil.isNotEmpty(this.modelInfo.possibilities)
    },
    handlePossibilitiesResponse (possibilities) {
      let result = []
      possibilities.forEach(possibility => {
        result.push({
          name: possibility.name,
          prefecture: possibility.prefecture,
          regionCode: possibility.region_code,
          standardNumber: possibility.standard_number
        })
      })
      return result
    }
  }
}
</script>

<style scoped>
  .model-train-input-container {
    padding: 24px;
    height: 100%;
  }

  .category-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .category {
    border: 1px solid #c0d5ef;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 350px;
    background: #e3ebf4;
    padding: 14px;
    margin: 6px;
  }

  .category.selected {
    background: #fff;
  }

  .category > p {
    margin: 0;
  }

  .category > .category-label {
    font-size: 0.88rem;
  }

  .category > .category-sample {
    font-size: 0.75rem;
    color: #999;
  }

  .category > button {
    max-width: 90%;
  }

  .category > button >>> span {
    padding-right: 20px;
  }

  .category > button .icon {
    margin-right: 4px;
  }

  .category:not(.selected) > button .icon {
    visibility: hidden;
  }
</style>



// WEBPACK FOOTER //
// src/views/model/train/input.vue